import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { xtreamApi } from '../services/xtreamApi';
import VideoPlayer from '../components/Player/VideoPlayer';
import FavoriteButton from '../components/Common/FavoriteButton';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Play, Star, Calendar, ArrowLeft, Share2, ChevronRight, Loader2, X, ThumbsUp } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SeriesDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [episodes, setEpisodes] = useState({});
  const [seasons, setSeasons] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [episodeProgress, setEpisodeProgress] = useState({});
  const [tmdbCast, setTmdbCast] = useState([]);
  const [similarSeries, setSimilarSeries] = useState([]);
  const [selectedActor, setSelectedActor] = useState(null);
  const [actorMovies, setActorMovies] = useState([]);
  const [loadingActor, setLoadingActor] = useState(false);

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        setIsLoading(true);
        const data = await xtreamApi.getSeriesInfo(id);
        if (data) {
          setSeries(data.info || {});
          setEpisodes(data.episodes || {});
          setSeasons(data.seasons || []);
          
          const episodeKeys = Object.keys(data.episodes || {});
          if (episodeKeys.length > 0) {
            setSelectedSeason(episodeKeys[0]);
          }

          const progress = await xtreamApi.getWatchProgress().catch(() => []);
          const epProgress = {};
          (progress || []).forEach(p => {
            if (p.content_type === 'series' && p.content_id === String(id) && p.episode_id) {
              epProgress[p.episode_id] = p;
            }
          });
          setEpisodeProgress(epProgress);

          // Fetch TMDB data - use tmdb_id if available, otherwise search by name
          const tmdbId = data.info?.tmdb_id;
          if (tmdbId) {
            xtreamApi.getTvCast(tmdbId).then(setTmdbCast).catch(() => {});
            xtreamApi.getSimilarSeries(tmdbId).then(setSimilarSeries).catch(() => {});
          } else if (data.info?.name) {
            xtreamApi.lookupTvByName(data.info.name).then(lookup => {
              if (lookup.cast) setTmdbCast(lookup.cast);
              if (lookup.similar) setSimilarSeries(lookup.similar);
            }).catch(() => {});
          }
        } else {
          setError('Series not found');
        }
      } catch (err) {
        console.error('Error fetching series:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSeries();
  }, [id]);

  const handleProgress = useCallback((currentTime, duration) => {
    if (!selectedEpisode || !series) return;
    xtreamApi.saveWatchProgress(
      id, 'series', series.name,
      selectedEpisode.info?.movie_image || series.cover,
      currentTime, duration,
      selectedSeason, selectedEpisode.episode_num,
      selectedEpisode.id
    ).catch(() => {});
  }, [id, series, selectedSeason, selectedEpisode]);

  const handleActorClick = async (actor) => {
    if (!actor.id) return;
    if (selectedActor?.id === actor.id) {
      setSelectedActor(null);
      setActorMovies([]);
      return;
    }
    setSelectedActor(actor);
    setLoadingActor(true);
    try {
      const movies = await xtreamApi.getActorMovies(actor.id);
      setActorMovies(movies);
    } catch {
      setActorMovies([]);
    }
    setLoadingActor(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading series..." />
      </div>
    );
  }

  if (error || !series) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">{error || 'Series not found'}</p>
          <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
        </div>
      </div>
    );
  }

  if (isPlaying && selectedEpisode) {
    const container = selectedEpisode.container_extension || 'mkv';
    const streamUrl = xtreamApi.getStreamUrl(selectedEpisode.id, 'series', container);
    const epProg = episodeProgress[String(selectedEpisode.id)];
    
    return (
      <div className="fixed inset-0 bg-black z-50">
        <VideoPlayer
          src={streamUrl}
          poster={selectedEpisode.info?.movie_image || series.cover}
          title={`${series.name} - S${selectedSeason}E${selectedEpisode.episode_num}`}
          onBack={() => { setIsPlaying(false); setSelectedEpisode(null); }}
          isLive={false}
          onProgress={handleProgress}
          initialTime={epProg?.current_time || 0}
        />
      </div>
    );
  }

  const handlePlayEpisode = (episode) => {
    setSelectedEpisode(episode);
    setIsPlaying(true);
  };

  const seasonKeys = Object.keys(episodes);
  const currentEpisodes = selectedSeason ? (episodes[selectedSeason] || []) : [];
  
  const backdrop = series.backdrop_path?.[0] || series.cover;
  const poster = series.cover;
  const title = series.name || 'Unknown';
  const rating = series.rating || '0';
  const year = series.releaseDate || '';
  const genre = series.genre || '';
  const plot = series.plot || 'No description available';
  const cast = series.cast || '';
  const castList = cast ? cast.split(',').map(a => a.trim()).filter(Boolean) : [];
  const hasTmdbCast = tmdbCast.length > 0;

  return (
    <div data-testid="series-detail-page" className="min-h-screen bg-[#0a0a0b]">
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[450px]">
        <img src={backdrop} alt={title} className="w-full h-full object-cover"
          onError={(e) => { e.target.src = poster || 'https://via.placeholder.com/1920x1080?text=No+Image'; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />

        <button onClick={() => navigate(-1)}
          className="absolute top-20 left-4 sm:left-8 flex items-center gap-2 text-white/70 hover:text-white transition-colors z-10">
          <ArrowLeft className="w-5 h-5" /><span>Back</span>
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 lg:p-16">
          <div className="max-w-[1800px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="hidden lg:block w-52 flex-shrink-0">
                <img src={poster} alt={title} className="w-full rounded-xl shadow-2xl"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x450?text=No+Poster'; }} />
              </div>

              <div className="flex-1 space-y-4">
                <h1 className="text-3xl sm:text-4xl font-bold text-white">{title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  {rating && rating !== '0' && (
                    <span className="flex items-center gap-1 text-yellow-400">
                      <Star className="w-4 h-4 fill-yellow-400" />{parseFloat(rating).toFixed(1)}/10
                    </span>
                  )}
                  {year && <span className="flex items-center gap-1 text-gray-300"><Calendar className="w-4 h-4" />{year}</span>}
                  <span className="text-gray-400">{seasonKeys.length} Season{seasonKeys.length !== 1 ? 's' : ''}</span>
                  {genre && (
                    <div className="flex gap-2 flex-wrap">
                      {genre.split(',').slice(0, 2).map((g, i) => (
                        <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-gray-300 text-xs">{g.trim()}</span>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-gray-300 text-sm max-w-2xl leading-relaxed">{plot}</p>
                <div className="flex flex-wrap gap-4 pt-2">
                  {currentEpisodes.length > 0 && (
                    <Button onClick={() => handlePlayEpisode(currentEpisodes[0])}
                      data-testid="series-play-btn"
                      className="bg-white text-black hover:bg-gray-200 px-8 py-5 text-base font-semibold">
                      <Play className="w-5 h-5 mr-2 fill-black" />Play S{selectedSeason}E1
                    </Button>
                  )}
                  <FavoriteButton contentId={id} contentType="series" name={title} poster={poster} rating={rating} size="lg"
                    className="border border-gray-500 text-white hover:bg-white/10 px-6 py-3 rounded-md flex items-center gap-2" />
                  <Button variant="ghost" className="text-white hover:bg-white/10 px-6 py-5"><Share2 className="w-5 h-5" /></Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-16">
        {/* Episodes Section */}
        <div className="py-8 border-b border-gray-800/50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Episodes</h2>
            {seasonKeys.length > 0 && (
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger className="w-40 bg-[#1a1a1d] border-gray-700 text-white">
                  <SelectValue placeholder="Select Season" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1d] border-gray-700">
                  {seasonKeys.map((season) => (
                    <SelectItem key={season} value={season} className="text-white hover:bg-white/10">
                      Season {season}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-3">
            {currentEpisodes.map((episode) => {
              const epProg = episodeProgress[String(episode.id)];
              return (
                <div key={episode.id} onClick={() => handlePlayEpisode(episode)}
                  data-testid={`episode-item-${episode.id}`}
                  className="flex items-center gap-4 p-4 bg-[#141417] rounded-xl hover:bg-[#1a1a1d] cursor-pointer transition-colors group relative overflow-hidden">
                  {epProg && epProg.progress_percent > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                      <div style={{backgroundColor:"var(--accent)"}} className="h-full" style={{ width: `${epProg.progress_percent}%` }} />
                    </div>
                  )}
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
                    <span className="text-2xl font-light text-gray-500 group-hover:hidden">{episode.episode_num}</span>
                    <Play className="w-8 h-8 text-white hidden group-hover:block" />
                  </div>
                  <div className="w-32 h-20 flex-shrink-0 bg-gray-800 rounded-lg overflow-hidden">
                    <img src={episode.info?.movie_image || series.cover} alt={episode.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/160x90?text=No+Thumb'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium">{episode.title || `Episode ${episode.episode_num}`}</h3>
                    <p className="text-gray-400 text-sm line-clamp-1 mt-1">{episode.info?.plot || `Episode ${episode.episode_num} of ${series.name}`}</p>
                  </div>
                  {episode.info?.duration && <span className="text-gray-500 text-sm flex-shrink-0">{episode.info.duration}</span>}
                </div>
              );
            })}
          </div>
          {currentEpisodes.length === 0 && (
            <div className="text-center py-12"><p className="text-gray-400">No episodes available for this season</p></div>
          )}
        </div>

        {/* Actors Section */}
        {(hasTmdbCast || castList.length > 0) && (
          <section data-testid="series-cast-section" className="py-8 border-b border-gray-800/50">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-5">
              <span style={{backgroundColor:"var(--accent)"}} className="w-1 h-5 rounded-full" />
              Actors
            </h2>
            {hasTmdbCast ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {tmdbCast.map((actor, i) => (
                  <div key={actor.id || i} data-testid={`series-cast-${i}`}
                    onClick={() => handleActorClick(actor)}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                      selectedActor?.id === actor.id ? '' : 'bg-[#141417] hover:bg-[#1a1a1d]'
                    }`}
                    style={selectedActor?.id === actor.id ? {backgroundColor:'var(--accent-bg)',boxShadow:'inset 0 0 0 1px var(--accent)'} : {}}>
                    <div className="w-14 h-14 rounded-full bg-gray-800 flex-shrink-0 overflow-hidden">
                      {actor.image ? (
                        <img src={actor.image} alt={actor.name} className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <div className={`w-full h-full items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800 ${actor.image ? 'hidden' : 'flex'}`}>
                        <span className="text-xl font-semibold text-gray-400">{actor.name.charAt(0)}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{actor.name}</p>
                      {actor.character && <p className="text-gray-500 text-xs truncate">{actor.character}</p>}
                    </div>
                    <ChevronRight className={`w-4 h-4 text-gray-600 flex-shrink-0 transition-transform ${selectedActor?.id === actor.id ? 'rotate-90' : ''}`} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
                {castList.map((actor, i) => (
                  <div key={i} className="flex-shrink-0 w-28 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center mb-2">
                      <span className="text-xl font-semibold text-gray-400">{actor.charAt(0)}</span>
                    </div>
                    <p className="text-gray-300 text-xs font-medium line-clamp-2 leading-tight">{actor}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Actor filmography */}
            {selectedActor && (
              <div data-testid="series-actor-filmography" className="mt-4 p-4 bg-[#111114] rounded-xl border border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    {selectedActor.image && <img src={selectedActor.image} alt="" className="w-8 h-8 rounded-full object-cover" />}
                    More from {selectedActor.name}
                  </h3>
                  <button onClick={() => { setSelectedActor(null); setActorMovies([]); }} className="text-gray-500 hover:text-white p-1"><X className="w-4 h-4" /></button>
                </div>
                {loadingActor ? (
                  <div className="flex justify-center py-6"><Loader2 style={{color:"var(--accent)"}} className="w-6 h-6 animate-spin" /></div>
                ) : actorMovies.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No other titles found</p>
                ) : (
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {actorMovies.map(m => (
                      <Link key={m.id} to={`/search?q=${encodeURIComponent(m.title)}`} className="flex-shrink-0 group">
                        <div className="relative w-28 rounded-lg overflow-hidden">
                          <div className="aspect-[2/3] bg-gray-800">
                            {m.poster ? (
                              <img src={m.poster} alt={m.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs p-2 text-center">{m.title}</div>
                            )}
                          </div>
                          {m.rating > 0 && (
                            <div className="absolute top-1 right-1 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/70 rounded text-[10px]">
                              <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" /><span className="text-white">{m.rating}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-300 text-[11px] mt-1.5 line-clamp-1 w-28">{m.title}</p>
                        <p className="text-gray-600 text-[10px] line-clamp-1 w-28">{m.character}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Similar Series */}
        {similarSeries.length > 0 && (
          <section data-testid="series-similar-section" className="py-8">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-5">
              <ThumbsUp style={{color:"var(--accent)"}} className="w-5 h-5" />
              You May Also Like
            </h2>
            <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
              {similarSeries.map((s) => (
                <Link key={s.id} to={`/search?q=${encodeURIComponent(s.name)}`} className="flex-shrink-0 group">
                  <div className="relative w-36 rounded-lg overflow-hidden">
                    <div className="aspect-[2/3] bg-gray-800">
                      {s.poster ? (
                        <img src={s.poster} alt={s.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs p-2 text-center">{s.name}</div>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                          <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                        </div>
                      </div>
                    </div>
                    {s.rating > 0 && (
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px]">
                        <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" /><span className="text-white">{s.rating}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-300 text-xs font-medium mt-2 line-clamp-1 w-36">{s.name}</p>
                  {s.year && <p className="text-gray-600 text-[10px] w-36">{s.year}</p>}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default SeriesDetailPage;
